import type { IDestination } from '@/entities/destination'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDestinationsQuery } from '@/entities/destination'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'

interface MasterPickerDialogProps {
  open: boolean
  excludedDestinationIds?: ReadonlySet<string>
  exclusionLoading?: boolean
  exclusionError?: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (destination: IDestination) => void
}

export function MasterPickerDialog({
  open,
  excludedDestinationIds,
  exclusionLoading = false,
  exclusionError = false,
  onOpenChange,
  onSelect,
}: MasterPickerDialogProps) {
  const { t } = useTranslation()
  const [keyword, setKeyword] = useState('')
  // * VI: Dialog luôn được mount nên query phải gate theo `open`, tránh tải 100 điểm đến khi người dùng chưa mở picker. | EN: The dialog is always mounted, so gate the query on `open` to avoid fetching 100 destinations before the picker is used.
  const destinationsQuery = useDestinationsQuery({ current: 1, pageSize: 100 }, { enabled: open })
  const destinations = useMemo(() => {
    if (exclusionLoading || exclusionError) {
      return []
    }

    const all = destinationsQuery.data?.data.data ?? []
    const addable = all.filter((item) => !excludedDestinationIds?.has(item.id))
    const normalized = keyword.trim().toLowerCase()

    if (!normalized) {
      return addable
    }

    return addable.filter((item) => {
      const localizedValues = item.localizedData ? Object.values(item.localizedData) : []
      return (
        item.slug.toLowerCase().includes(normalized) ||
        localizedValues.some((value) => (value.name ?? '').toLowerCase().includes(normalized))
      )
    })
  }, [
    destinationsQuery.data?.data,
    excludedDestinationIds,
    exclusionError,
    exclusionLoading,
    keyword,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('tenantDestination.pickFromPool', { defaultValue: 'Pick destination from pool' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder={t('common.search', { defaultValue: 'Search' })}
          />
          <div className="max-h-[420px] space-y-2 overflow-y-auto rounded-md border p-2">
            {(destinationsQuery.isPending || exclusionLoading) && (
              <p className="px-3 py-4 text-sm text-zinc-500">
                {t('common.loading', { defaultValue: 'Loading...' })}
              </p>
            )}

            {!destinationsQuery.isPending && !exclusionLoading && exclusionError && (
              <p className="px-3 py-4 text-sm text-zinc-500">
                {t('common.operationFailed', {
                  defaultValue: 'Operation failed. Please try again.',
                })}
              </p>
            )}

            {!destinationsQuery.isPending &&
              !exclusionLoading &&
              !exclusionError &&
              destinations.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item)}
                  className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <div>
                    <p className="font-medium">
                      {Object.values(item.localizedData ?? {})[0]?.name ?? item.slug}
                    </p>
                    <p className="text-xs text-zinc-500">{item.slug}</p>
                  </div>
                  <span className="text-xs text-zinc-500">{item.destinationType ?? '-'}</span>
                </button>
              ))}

            {!destinationsQuery.isPending &&
              !exclusionLoading &&
              !exclusionError &&
              destinations.length === 0 && (
                <p className="px-3 py-4 text-sm text-zinc-500">
                  {t('common.noData', { defaultValue: 'No data' })}
                </p>
              )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
